import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, errorText, type InputStatusDto, type PlanDto } from "../api/client";
import { CaptureBox } from "../capture/CaptureBox";
import { rejectText } from "../capture/uploads";
import { Questions } from "../components/Questions";
import { PlanDecision } from "../components/PlanDecision";
import { relativeTime } from "../format";

export const statusLabels: Record<string, string> = {
  waiting_for_assets: "Dateien werden übernommen",
  queued: "In Warteschlange",
  transcribing: "Wird transkribiert",
  awaiting_transcript_confirmation: "Transkript prüfen",
  interpreting: "Wird ausgewertet",
  needs_answer: "Rückfrage",
  awaiting_review: "Vorschlag",
  committed: "Eingetragen",
  rejected: "Verworfen",
  failed: "Fehlgeschlagen",
  ai_unavailable: "KI nicht verfügbar",
  cancelled: "Abgebrochen",
  no_action: "Keine Aktion erkannt",
};

const channelLabels: Record<string, string> = { pwa_text: "Text", pwa_voice: "Spracheingabe", voice_assistant: "Spracheingabe", pwa_upload: "Bild", mcp: "ChatGPT/Codex" };

/** Der Eingangskorb zeigt für jeden Eingang den tatsächlichen Status, die Quelle und die nächste Aktion. */
const DONE = ["cancelled", "rejected"];
/** Noch nicht gespeicherte Eingänge, die gelöscht (verworfen) werden können – auch wartende (z. B. wenn der Worker steht). */
const DELETABLE = ["queued", "waiting_for_assets", "needs_answer", "awaiting_review", "awaiting_transcript_confirmation", "failed", "ai_unavailable", "no_action"];

export function InboxPage() {
  const [showDone, setShowDone] = useState(false);
  const inputs = useQuery({
    queryKey: ["inputs"],
    queryFn: () => api<InputStatusDto[]>("/api/v1/inputs?limit=40"),
    refetchInterval: (q) => (q.state.data?.some((i) => ["queued", "transcribing", "interpreting", "waiting_for_assets"].includes(i.status)) ? 3000 : false),
  });

  return (
    <div className="page">
      <h1>Eingang</h1>
      <CaptureBox compact />
      {inputs.isError && <p className="error">{errorText(inputs.error)}</p>}
      <ul className="inbox">
        {inputs.data?.filter((i) => showDone || !DONE.includes(i.status)).map((i) => <InputCard key={i.inputId} input={i} />)}
      </ul>
      {inputs.data?.length === 0 && <p className="muted">Noch keine Eingänge.</p>}
      {inputs.data?.some((i) => DONE.includes(i.status)) && (
        <button className="btn ghost" onClick={() => setShowDone(!showDone)}>{showDone ? "Gelöschte ausblenden" : "Auch gelöschte/verworfene anzeigen"}</button>
      )}
    </div>
  );
}

function InputCard({ input }: { input: InputStatusDto }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [transcript, setTranscript] = useState(input.transcript ?? "");
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries();

  const confirm = useMutation({
    mutationFn: () => api(`/api/v1/inputs/${input.inputId}/transcript`, { body: { transcript, expectedVersion: Number(input.version) } }),
    onSuccess: refresh,
    onError: (e) => setError(errorText(e)),
  });
  const retry = useMutation({ mutationFn: () => api(`/api/v1/inputs/${input.inputId}/retry`, { method: "POST", body: {} }), onSuccess: refresh, onError: (e) => setError(errorText(e)) });
  const cancel = useMutation({ mutationFn: () => api(`/api/v1/inputs/${input.inputId}/cancel`, { method: "POST", body: {} }), onSuccess: refresh, onError: (e) => setError(errorText(e)) });
  const reverse = useMutation({
    mutationFn: () => api<PlanDto>(`/api/v1/commits/${input.receipt!.commitId}/propose-reversal`, { method: "POST", body: {} }),
    onSuccess: (plan) => navigate(`/review/${plan.id}`),
    onError: (e) => setError(errorText(e)),
  });

  return (
    <li className={`card input status-${input.status}`} id={input.inputId}>
      <div className="row between">
        <span className={`status-chip s-${input.status}`}>{input.status === "needs_answer" && !input.questions.some(q => !q.answer) ? "Angaben prüfen" : statusLabels[input.status] ?? input.status}</span>
        <span className="muted small">
          {channelLabels[input.channel ?? ""] ?? input.channel} · {relativeTime(input.receivedAt)}
        </span>
      </div>
      {input.text && <p className="input-text">{input.text}</p>}
      {input.clientExtractedSummary && <p className="muted small">Vom Client extrahiert (Original nicht archiviert): {input.clientExtractedSummary}</p>}
      {input.assets.length > 0 && (
        <div className="thumbs">
          {input.assets.map((a) =>
            a.hasPreview ? (
              <img key={a.id} src={`/api/v1/assets/${a.id}/preview`} alt="Quelle" loading="lazy" />
            ) : (
              <span key={a.id} className="thumb-file">{a.kind === "audio" ? "🎙️ Aufnahme" : a.status === "rejected" ? `⚠️ ${rejectText(a.rejectCode)}` : "📄"}</span>
            ),
          )}
        </div>
      )}
      {input.statusMessage && <p className={input.status === "failed" || input.status === "ai_unavailable" ? "error" : "muted"}>{input.statusMessage}</p>}
      {input.anchorSource !== "client" && input.anchorSource !== "server" && <p className="muted small">Hinweis: Erfassungszeit des Geräts war unplausibel – Bezugszeit ist der Eingang beim Server.</p>}

      {input.status === "awaiting_transcript_confirmation" && (
        <div className="transcript">
          <label>
            Erkannter Text (bitte prüfen)
            <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={3} />
          </label>
          <button className="btn primary" disabled={confirm.isPending || !transcript.trim()} onClick={() => confirm.mutate()}>Weiter auswerten</button>
        </div>
      )}
      {input.status === "needs_answer" && !input.planId && <Questions inputId={input.inputId} questions={input.questions} />}
      {["awaiting_review", "needs_answer"].includes(input.status) && input.planId && <PlanDecision planId={input.planId} />}
      {input.status === "committed" && input.receipt && (
        <div className="row between receipt">
          <span className="ok small">Gespeichert · Beleg {input.receipt.commitId.slice(0, 8)} · Rev. {String(input.receipt.revision)}</span>
          <button className="btn ghost" disabled={reverse.isPending} onClick={() => reverse.mutate()}>Rückgängig …</button>
        </div>
      )}
      {input.status === "needs_answer" && !input.planId && (
        <div className="row">
          <button className="btn" disabled={retry.isPending} onClick={() => retry.mutate()} title="Statt Rückfragen einen Vorschlag zum Bearbeiten erstellen">Neu auswerten</button>
        </div>
      )}
      {["failed", "ai_unavailable", "no_action"].includes(input.status) && (
        <div className="row">
          <button className="btn" disabled={retry.isPending} onClick={() => retry.mutate()}>Erneut versuchen</button>
          <Link className="btn" to="/calendar">Manuell eintragen</Link>
        </div>
      )}
      {["queued", "transcribing", "interpreting", "waiting_for_assets"].includes(input.status) && (
        <div className="row between">
          <span className="muted small">Wird verarbeitet – du kannst die Seite verlassen.</span>
          <button className="btn ghost" onClick={() => cancel.mutate()}>Abbrechen</button>
        </div>
      )}
      {DELETABLE.includes(input.status) && !(["awaiting_review", "needs_answer"].includes(input.status) && input.planId) && (
        <div className="row end">
          <button className="btn ghost danger-text" disabled={cancel.isPending}
            onClick={() => window.confirm("Diesen Eintrag löschen? Es wird nichts gespeichert.") && cancel.mutate()}>
            🗑 Löschen
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </li>
  );
}
